/**
 * ESTADÍSTICAS DE HORAS DE VUELO
 *
 * Las MISMAS horas, cortadas de cuatro maneras: por aeronave, por piloto, por
 * empresa y por cliente. Los cuatro cortes tienen que dar el mismo total para
 * el mismo período —si no dan, uno de los cuatro está filtrando distinto— y eso
 * es una de las cosas que las pruebas fijan.
 *
 * ═══ LA REGLA QUE MANDA SOBRE TODAS ═══
 *
 * **El corte por empresa agrupa por el VUELO, no por el dueño del equipo.**
 *
 * Un equipo puede cambiar de empresa y se lleva sus horas y sus partes: eso es
 * la realidad del mantenimiento, porque las horas están en la célula. Pero los
 * vuelos YA HECHOS se volaron para la empresa de entonces. Si este informe
 * agrupara por el dueño actual de la aeronave, transferir un equipo
 * **reescribiría meses de historia**: horas voladas para la empresa A pasarían a
 * contarse en la B, sin que nadie volara nada distinto.
 *
 * Por eso el asiento guarda `empresaId` —o su cliente, que ya dice de qué
 * empresa es— y la transferencia es un hecho aparte, con fecha.
 *
 * ═══ DE DÓNDE SALE CADA NÚMERO ═══
 *
 * Todos los cortes devuelven, además de las filas: cuántos asientos entraron,
 * cuántos quedaron afuera porque el técnico marcó que discrepan del papel, y
 * cuántos no tienen fecha y por eso no se pueden ubicar en ningún período.
 * Un total sin su procedencia no es un informe: es un número en el que hay que
 * confiar sin motivo.
 */
import { ESTADOS_COTEJO, enPeriodo, evaluarCotejo } from "./bitacora.js";

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const redondear = (n) => Math.round(n * 100) / 100;
const filas = (b) => (Array.isArray(b) ? b : []);

export const SIN_EMPRESA = "(sin empresa)";
export const SIN_CLIENTE = "(sin cliente)";
export const SIN_ASIGNAR = "(sin asignar)";

/**
 * Parte la bitácora en lo que cuenta y lo que no, para el período pedido.
 *
 * Un asiento cuenta si tiene fecha dentro del período y el técnico no lo marcó
 * como discrepante. Los discrepantes se excluyen —igual que en el estado de
 * mantenimiento— y se informan aparte: seguir sumando horas de un asiento que se
 * sabe distinto del papel es inflar el respaldo con un dato que ya se descartó.
 */
function partir(bitacora, { desde = null, hasta = null } = {}) {
  const todas = filas(bitacora).filter(Boolean);
  const sinFecha = todas.filter((v) => !v.fecha).length;
  const enRango = todas.filter((v) => v.fecha && enPeriodo(v.fecha, desde, hasta));
  const contados = enRango.filter((v) => evaluarCotejo(v.cotejo).estado !== ESTADOS_COTEJO.DISCREPA);
  return { contados, excluidos: enRango.length - contados.length, sinFecha };
}

function acumular(asientos) {
  const fechas = [...new Set(asientos.map((v) => String(v.fecha).slice(0, 10)))].sort();
  return {
    horas: redondear(asientos.reduce((s, v) => s + num(v.horasAeronave), 0)),
    horasMotor: redondear(asientos.reduce((s, v) => s + num(v.horasMotor), 0)),
    aterrizajes: asientos.reduce((s, v) => s + num(v.ldg), 0),
    cargas: asientos.reduce((s, v) => s + num(v.cargas), 0),
    vuelos: asientos.length,
    /* Días de CALENDARIO, no salidas: tres vuelos el mismo día son un día
       volado. Es la misma cuenta que usa el duty time, y por la misma razón. */
    dias: fechas.length,
  };
}

/**
 * La tabla de un corte. Los cuatro cortes salen de acá para que no puedan
 * discrepar en el filtro, en el redondeo ni en la procedencia: lo único que
 * cambia es por qué se agrupa.
 */
function tabla(bitacora, { desde = null, hasta = null, claveDe, nombreDe = null, filtrar = null } = {}) {
  const { contados, excluidos, sinFecha } = partir(bitacora, { desde, hasta });
  const propios = filtrar ? contados.filter(filtrar) : contados;

  const grupos = new Map();
  for (const v of propios) {
    const clave = claveDe(v);
    if (!grupos.has(clave)) grupos.set(clave, []);
    grupos.get(clave).push(v);
  }

  const resultado = [...grupos.entries()]
    .map(([clave, asientos]) => ({
      clave,
      nombre: nombreDe ? (nombreDe(clave) || clave || SIN_ASIGNAR) : (clave || SIN_ASIGNAR),
      ...acumular(asientos),
    }))
    .sort((a, b) => b.horas - a.horas || String(a.nombre).localeCompare(String(b.nombre)));

  return {
    filas: resultado,
    total: acumular(propios),
    /* Lo que no entró, dicho: sin esto el total es un número sin respaldo. */
    procedencia: {
      delPeriodo: propios.length,
      excluidosPorDiscrepancia: excluidos,
      sinFecha,
    },
  };
}

function nombrePorId(lista, id, campo) {
  const encontrado = filas(lista).find((x) => x && (x.id === id || x.clave === id));
  return encontrado ? (encontrado[campo] || encontrado.nombre) : null;
}

/** De qué empresa es un vuelo: la que dice el asiento, o la del cliente. */
export function empresaDelVuelo(vuelo, clientes = []) {
  if (!vuelo) return null;
  if (vuelo.empresaId) return vuelo.empresaId;
  const cliente = filas(clientes).find((c) => c && c.id === vuelo.clienteId);
  return cliente?.empresaId || null;
}

export function horasPorAeronave(bitacora, aeronaves = [], opciones = {}) {
  const { empresaId = null, clientes = [], ...periodo } = opciones;
  return tabla(bitacora, {
    ...periodo,
    claveDe: (v) => v.aircraftId || null,
    nombreDe: (clave) => nombrePorId(aeronaves, clave, "matricula"),
    filtrar: empresaId
      ? (v) => empresaDelVuelo(v, clientes) === empresaId
      : null,
  });
}

/**
 * Horas por piloto. **Suman entre empresas**: un piloto puede volar para varias
 * y su total es el de todas —es lo que respalda su licencia— mientras el
 * desglose por empresa es lo que le importa a cada operador.
 */
export function horasPorPiloto(bitacora, pilotos = [], opciones = {}) {
  const { empresaId = null, clientes = [], ...periodo } = opciones;
  const base = tabla(bitacora, {
    ...periodo,
    claveDe: (v) => v.pilotId || null,
    nombreDe: (clave) => {
      const p = filas(pilotos).find((x) => x && x.id === clave);
      if (!p) return null;
      return [p.nombre, p.apellido].filter(Boolean).join(" ") || p.nombre;
    },
    filtrar: empresaId ? (v) => empresaDelVuelo(v, clientes) === empresaId : null,
  });

  /* El desglose por empresa se calcula sobre los asientos de cada piloto, y
     suma lo mismo que su total: es la misma lista, contada de otra forma. */
  const { contados } = partir(bitacora, periodo);
  const conDesglose = base.filas.map((fila) => {
    const suyos = contados.filter((v) => (v.pilotId || null) === fila.clave);
    const porEmpresa = new Map();
    for (const v of suyos) {
      const e = empresaDelVuelo(v, clientes) || SIN_EMPRESA;
      porEmpresa.set(e, (porEmpresa.get(e) || 0) + num(v.horasAeronave));
    }
    return {
      ...fila,
      empresas: [...porEmpresa.entries()]
        .map(([clave, horas]) => ({ clave, nombre: nombrePorId(opciones.empresas, clave, "nombre") || clave, horas: redondear(horas) }))
        .sort((a, b) => b.horas - a.horas),
    };
  });

  return { ...base, filas: conDesglose };
}

/** Las empresas para las que voló un piloto. No se declaran: lo dice el vuelo. */
export function empresasDePiloto(bitacora, pilotId, { clientes = [] } = {}) {
  const vistas = new Set();
  for (const v of filas(bitacora)) {
    if (!v || v.pilotId !== pilotId) continue;
    vistas.add(empresaDelVuelo(v, clientes) || SIN_EMPRESA);
  }
  return [...vistas].sort();
}

export function horasPorEmpresa(bitacora, empresas = [], opciones = {}) {
  const { clientes = [], ...periodo } = opciones;
  return tabla(bitacora, {
    ...periodo,
    claveDe: (v) => empresaDelVuelo(v, clientes) || SIN_EMPRESA,
    nombreDe: (clave) => nombrePorId(empresas, clave, "nombre"),
  });
}

/**
 * Horas por cliente. Los vuelos SIN cliente no desaparecen: se informan aparte,
 * porque un vuelo de traslado propio o de instrucción es un vuelo y sus horas
 * están en el total — sacarlo de la tabla sin decirlo haría que las filas no
 * sumaran el total y nadie sabría por qué.
 */
export function horasPorCliente(bitacora, clientes = [], opciones = {}) {
  const { empresaId = null, ...periodo } = opciones;
  const resultado = tabla(bitacora, {
    ...periodo,
    claveDe: (v) => v.clienteId || null,
    nombreDe: (clave) => nombrePorId(clientes, clave, "nombre"),
    filtrar: empresaId
      ? (v) => empresaDelVuelo(v, clientes) === empresaId
      : null,
  });

  const sinCliente = resultado.filas.find((f) => f.clave === null) || null;
  return {
    ...resultado,
    filas: resultado.filas.filter((f) => f.clave !== null),
    sinCliente,
  };
}

/* ─────────────────────────────────────────────────────────────────────────────
 * FACTURACIÓN — horas × tarifa, sin inventar la tarifa
 *
 * La tarifa es un dato del operador, no una constante del programa. Por eso:
 *
 *   · no hay tarifa por defecto;
 *   · si un vuelo no tiene tarifa vigente, sus horas se informan como «sin
 *     tarifa» y NO se multiplican por cero (un cero se lee como «gratis»);
 *   · si en el período hay tarifas en dos monedas, no se suman entre sí: se
 *     muestran por separado.
 *
 * Las horas son las mismas del informe: air time de los asientos que no
 * discrepan del papel. Cambiar la definición acá haría que la factura y el
 * informe de horas dijeran cosas distintas.
 * ──────────────────────────────────────────────────────────────────────────── */

/** La tarifa que regía para un cliente en una fecha. `null` si no hay. */
export function tarifaVigente(tarifas = [], clienteId, fecha = null) {
  if (!clienteId) return null;

  const candidatas = filas(tarifas)
    .filter((t) => t && t.clienteId === clienteId)
    .filter((t) => t.desde && (!fecha || String(t.desde) <= String(fecha)))
    /* `Number(null)` y `Number("")` dan 0: sin esta guarda, una tarifa vacía
       se escondería como «cero por hora», que es justo el número que no se
       quiere inventar. */
    .filter((t) => t.valorHora !== null && t.valorHora !== "")
    .filter((t) => Number.isFinite(Number(t.valorHora)) && Number(t.valorHora) >= 0)
    .filter((t) => typeof t.moneda === "string" && t.moneda.trim() !== "")
    .sort((a, b) => String(a.desde).localeCompare(String(b.desde)));

  const t = candidatas[candidatas.length - 1];
  return t
    ? {
        valorHora: Number(t.valorHora),
        moneda: t.moneda,
        desde: t.desde,
        fuente: t.fuente || null,
      }
    : null;
}

/**
 * Factura las horas de un período.
 *
 * Cada vuelo usa la tarifa vigente EN SU FECHA: una tarifa que cambió a mitad
 * de mes no se aplica hacia atrás a los vuelos ya volados.
 */
export function facturar(bitacora = [], {
  clientes = [],
  tarifas = [],
  desde = null,
  hasta = null,
  empresaId = null,
} = {}) {
  const { contados, excluidos, sinFecha } = partir(bitacora, { desde, hasta });
  const propios = empresaId
    ? contados.filter((v) => empresaDelVuelo(v, clientes) === empresaId)
    : contados;

  const porCliente = new Map();
  const sinCliente = { horas: 0, vuelos: 0 };

  for (const v of propios) {
    const horas = num(v.horasAeronave);

    if (!v.clienteId) {
      sinCliente.horas += horas;
      sinCliente.vuelos += 1;
      continue;
    }

    if (!porCliente.has(v.clienteId)) {
      porCliente.set(v.clienteId, {
        clienteId: v.clienteId,
        nombre: nombrePorId(clientes, v.clienteId, "nombre") || v.clienteId,
        horas: 0,
        cubiertas: 0,
        sinTarifa: 0,
        vuelos: 0,
        monedas: new Map(),
      });
    }

    const fila = porCliente.get(v.clienteId);
    fila.horas += horas;
    fila.vuelos += 1;

    const tarifa = tarifaVigente(tarifas, v.clienteId, String(v.fecha).slice(0, 10));
    if (!tarifa) {
      fila.sinTarifa += horas;
      continue;
    }

    fila.cubiertas += horas;
    fila.monedas.set(
      tarifa.moneda,
      (fila.monedas.get(tarifa.moneda) || 0) + horas * tarifa.valorHora,
    );
  }

  const filasFactura = [...porCliente.values()]
    .map((f) => {
      const monedas = [...f.monedas.entries()]
        .map(([moneda, importe]) => ({ moneda, importe: redondear(importe) }))
        .sort((a, b) => b.importe - a.importe);
      return {
        ...f,
        horas: redondear(f.horas),
        cubiertas: redondear(f.cubiertas),
        sinTarifa: redondear(f.sinTarifa),
        completa: f.sinTarifa === 0,
        /* Un importe único solo existe si toda la factura está en una moneda.
           Si hay dos, el número único sería una suma de cosas distintas. */
        importe: monedas.length === 1 ? monedas[0].importe : null,
        moneda: monedas.length === 1 ? monedas[0].moneda : null,
        porMoneda: monedas,
      };
    })
    .sort((a, b) => a.nombre.localeCompare(b.nombre));

  const totales = new Map();
  for (const f of porCliente.values()) {
    for (const [moneda, importe] of f.monedas) {
      totales.set(moneda, (totales.get(moneda) || 0) + importe);
    }
  }

  return {
    filas: filasFactura,
    totales: [...totales.entries()]
      .map(([moneda, importe]) => ({ moneda, importe: redondear(importe) }))
      .sort((a, b) => b.importe - a.importe),
    sinCliente: { horas: redondear(sinCliente.horas), vuelos: sinCliente.vuelos },
    /* Todo lo que no se puede cobrar, junto: horas de clientes sin tarifa y
       horas de vuelos sin cliente. */
    horasSinTarifa: redondear(
      filasFactura.reduce((s, f) => s + f.sinTarifa, 0) + sinCliente.horas,
    ),
    procedencia: {
      asientos: propios.length,
      excluidosPorDiscrepancia: excluidos,
      sinFecha,
      sinCliente: sinCliente.vuelos,
    },
  };
}

/* Acá vivía `clientesDeEmpresa`, con su prueba. Se retiró en la e138.

   El catálogo de clientes tiene su módulo —`src/domain/clientes.js`— y la misma
   consulta escrita en los dos lados son dos definiciones de «el cliente de una
   empresa». Alcanza con que una filtre distinto —una con `activo !== false` y la
   otra sin eso— para que el informe muestre las horas de un cliente dado de baja
   en un lado y no en el otro, y las dos versiones parecen razonables. */

export function totalesDelPeriodo(bitacora, opciones = {}) {
  const { contados, excluidos, sinFecha } = partir(bitacora, opciones);
  return {
    ...acumular(contados),
    procedencia: { asientos: contados.length, excluidosPorDiscrepancia: excluidos, sinFecha },
  };
}

/* ─────────────────────────────────────────────────────────────────────────────
 * LA TRANSFERENCIA DE UN EQUIPO
 *
 * El equipo tiene un dueño ACTUAL —el que importa para el mantenimiento y para
 * decidir quién lo opera— y una lista de asignaciones con fecha. La lista es lo
 * que permite contestar «¿de quién era este equipo en marzo?», que es la
 * pregunta que aparece cuando hay que explicar un vuelo de hace seis meses.
 * ──────────────────────────────────────────────────────────────────────────── */

export function asignarEquipo(asignaciones = [], { aeronaveId, empresaId, desde, quien = null } = {}) {
  if (!aeronaveId || !empresaId || !desde) {
    throw new Error("Para asignar un equipo hacen falta la aeronave, la empresa y la fecha.");
  }
  return [
    ...filas(asignaciones),
    { aeronaveId, empresaId, desde, quien, el: new Date().toISOString() },
  ].sort((a, b) => String(a.desde).localeCompare(String(b.desde)));
}

/** El dueño del equipo en una fecha: la última asignación que ya había empezado. */
export function empresaDelEquipo(asignaciones = [], aeronaveId, fecha = null) {
  const suyas = filas(asignaciones)
    .filter((a) => a && a.aeronaveId === aeronaveId)
    .filter((a) => !fecha || String(a.desde) <= String(fecha))
    .sort((a, b) => String(a.desde).localeCompare(String(b.desde)));
  return suyas.length ? suyas[suyas.length - 1].empresaId : null;
}
